// iuFinderMerge.js — merge the backend (saved HARs) and proxy (live buffer)
// answers for the IU Finder page. Pure. docs/iu-finder-spec.md Contract C/D.
//
// Requests/pages are NEVER summed across sources: a live row that was also
// saved into a HAR would count twice. Each source keeps its own numbers.

const MAX_EXAMPLES = 5;

const union = (a, b) => Array.from(new Set([...(a || []), ...(b || [])]));
const num = (v) => (Number.isFinite(v) ? v : 0);

function srcCounts(iu) {
  return iu ? { requests: num(iu.requests), pages: num(iu.pages) } : null;
}

/**
 * mergeAnswers({ hars, live }) — either may be null / `{ error }` / undefined.
 * -> { results, coverage, partialRows, unreadableRows }
 */
export function mergeAnswers({ hars, live } = {}) {
  const ok = (a) => (a && !a.error && Array.isArray(a.results) ? a : null);
  const h = ok(hars);
  const l = ok(live);
  const byTemplate = new Map();

  const take = (ans, key) => {
    if (!ans) return;
    for (const r of ans.results) {
      let t = byTemplate.get(r.template);
      if (!t) {
        t = { template: r.template, matchedOn: [], drift: false, partialRows: 0, isNew: false, firstSeen: null,
          requestsBySource: { hars: 0, live: 0 }, ius: new Map(), examples: { hars: [], live: [] } };
        byTemplate.set(r.template, t);
      }
      t.matchedOn = union(t.matchedOn, r.matchedOn);
      t.drift = t.drift || !!r.drift;
      // "New" comes from the HAR index only (live rows have no history to compare)
      if (key === 'hars' && r.isNew) { t.isNew = true; t.firstSeen = r.firstSeen; }
      t.partialRows = Math.max(t.partialRows, num(r.partialRows)); // never summed: a live row may also be in a HAR
      t.requestsBySource[key] = num(r.requests);
      for (const iu of r.ius || []) {
        let m = t.ius.get(iu.iu);
        if (!m) {
          m = { iu: iu.iu, bySource: { hars: null, live: null }, platforms: [], appVersions: [], sizes: [], lastSeen: null,
            targeting: { hars: null, live: null }, samples: [] };
          t.ius.set(iu.iu, m);
        }
        m.bySource[key] = srcCounts(iu);
        m.platforms = union(m.platforms, iu.platforms);
        m.appVersions = union(m.appVersions, iu.appVersions);
        m.sizes = union(m.sizes, iu.sizes);
        if (iu.lastSeen != null && (m.lastSeen == null || iu.lastSeen > m.lastSeen)) m.lastSeen = iu.lastSeen;
        // Targeting stays per source, like the counts: a live row also saved into
        // a HAR would otherwise be counted twice. The panel shows one source.
        m.targeting[key] = iu.targeting || null;
        for (const s of iu.samples || []) m.samples.push({ ...s, source: key });
      }
      for (const ex of r.examples || []) t.examples[key].push({ ...ex, source: key });
    }
  };
  take(h, 'hars');
  take(l, 'live');

  const best = (i) => Math.max(i.bySource.hars ? i.bySource.hars.requests : 0, i.bySource.live ? i.bySource.live.requests : 0);
  const results = Array.from(byTemplate.values()).map((t) => ({
    template: t.template,
    matchedOn: t.matchedOn,
    drift: t.drift,
    isNew: t.isNew,
    firstSeen: t.firstSeen,
    partialRows: t.partialRows,
    requests: Math.max(t.requestsBySource.hars, t.requestsBySource.live),
    ius: Array.from(t.ius.values())
      .map((i) => ({ ...i, samples: i.samples.slice().sort((a, b) => (b.ts || 0) - (a.ts || 0)).slice(0, MAX_EXAMPLES) }))
      .sort((a, b) => best(b) - best(a) || String(a.iu).localeCompare(String(b.iu))),
    examples: [...t.examples.hars, ...t.examples.live].slice(0, MAX_EXAMPLES),
  }));
  results.sort((a, b) => b.requests - a.requests || a.template.localeCompare(b.template));

  const errMsg = (a) => (a == null ? null : a.error ? String(a.error.message || a.error) : null);
  const cov = (a) => (a && a.coverage) || {};
  return {
    results,
    partialRows: Math.max(num(h && h.partialRows), num(l && l.partialRows)),
    unreadableRows: num(h && h.unreadableRows) + num(l && l.unreadableRows),
    coverage: {
      hars: h ? (Number.isFinite(cov(h).hars) ? cov(h).hars : 0) : null,
      harList: h && Array.isArray(cov(h).harList) ? cov(h).harList : [],
      liveRows: l ? num(cov(l).liveRows) : null,
      gamRows: l ? num(cov(l).gamRows) : null,
      harsError: errMsg(hars),
      liveError: errMsg(live),
    },
  };
}

/**
 * harLabel(meta, id) — what a person calls a saved HAR: its title (or host)
 * and the day it was saved, e.g. "Fox News iOS · Aug 5". The 8-char id is
 * the fallback only when the HAR has neither.
 */
export function harLabel(meta, id) {
  const m = meta || {};
  const d = m.createdAt != null ? new Date(m.createdAt) : null;
  const day = d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
  const name = String(m.title || '').trim();
  if (name) return day ? `${name} · ${day}` : name;
  const short = `HAR ${String(id || m.id || '').slice(0, 8)}`;
  return day ? `${short} · ${day}` : short;
}

/**
 * pickTargeting(iu) -> { source, t } | null — the source whose targeting saw
 * more requests (never a sum; see mergeAnswers). Null when neither has any.
 */
export function pickTargeting(iu) {
  const h = iu && iu.targeting && iu.targeting.hars;
  const l = iu && iu.targeting && iu.targeting.live;
  const rows = (t) => (t && Number.isFinite(t.rows) ? t.rows : 0);
  if (!rows(h) && !rows(l)) return null;
  return rows(l) > rows(h) ? { source: 'live', t: l } : { source: 'hars', t: h };
}

function csvField(v) {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const HEADER = ['template', 'iu', 'har_requests', 'har_pages', 'live_requests', 'live_pages',
  'platforms', 'app_versions', 'sizes', 'last_seen_iso', 'drift'];

/** toCsv(merged) -> CSV text, one row per template x IU. */
export function toCsv(merged) {
  const lines = [HEADER.join(',')];
  for (const r of (merged && merged.results) || []) {
    for (const i of r.ius) {
      const ts = i.lastSeen == null ? '' : (() => { const d = new Date(i.lastSeen); return Number.isNaN(d.getTime()) ? '' : d.toISOString(); })();
      lines.push([
        r.template, i.iu,
        i.bySource.hars ? i.bySource.hars.requests : '', i.bySource.hars ? i.bySource.hars.pages : '',
        i.bySource.live ? i.bySource.live.requests : '', i.bySource.live ? i.bySource.live.pages : '',
        i.platforms.join('|'), i.appVersions.join('|'), i.sizes.join('|'), ts, r.drift ? 'true' : 'false',
      ].map(csvField).join(','));
    }
  }
  return lines.join('\n') + '\n';
}
